import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { useViewport } from '../../hooks/useViewport';
import { ADMIN_NAV, CONSOLE_NAV } from './navModel';
import { Glyph } from './glyphs';
import { MobileTopBar } from './MobileTopBar';
import { NavDrawer } from './NavDrawer';
import { Sidebar } from './Sidebar';
import { SkipLink, MAIN_ID } from './SkipLink';
import { ThemeToggle } from './ThemeToggle';
import { UtilityBar } from './UtilityBar';
import './shells.css';

const DRAWER_ID = 'console-nav-drawer';

/**
 * ConsoleShell, the MANAGING shell.
 *
 * Dashboard, trade desk, create listing, settings, and every admin route. This
 * is where a user owns things rather than desires them, so the nav is a
 * persistent grouped sidebar and the page is a working surface.
 *
 * THE ADMIN VARIANT IS A VARIANT, NOT A FORK. `variant="admin"` swaps the nav
 * groups and adds an identity badge to the brand line. Nothing else changes,
 * and there is no second component. Pass `navGroups` to override entirely.
 *
 * WHAT BRANCHES ON `useViewport()` AND WHY
 * ----------------------------------------
 *   desktop  `.dsk-sidebar` (persistent, 260px) + `.dsk-utilitybar` + `.dsk-page`
 *   mobile   `.ds-topbar` with a drawer trigger, no utility bar, and the
 *            sidebar as a modal `.ds-navdrawer` mounted only while open
 *
 * A persistent sidebar and a modal drawer are not the same element at two
 * widths. The drawer needs a scrim, a focus trap, Escape, a scroll lock and a
 * close button; the sidebar needs none of those and must never be a dialog. And
 * the utility bar does not shrink, it collapses: its search moves into the
 * drawer and its primary action becomes the header's trailing icon button.
 *
 * WIDTH. `.dsk-page` is the 1280px reading container. The console templates run
 * 1440 deliberately, because a 260px sidebar eats into the page and a
 * management table needs the rest, and the library exposes that as the opt-in
 * `.dsk-page--console` modifier. `wide` is how a page opts in, and it is on by
 * default here because a console page is exactly what the modifier is for. Pass
 * `wide={false}` for a narrow reading page such as a settings article.
 *
 * @param {{
 *   children: React.ReactNode,
 *   variant?: 'console'|'admin',
 *   navGroups?: Array<{ id: string, label?: string, items: Array<object> }>,
 *   breadcrumbs?: Array<{ label: string, to?: string }>,
 *   title?: string,
 *   search?: { value?: string, onChange?: Function, onSubmit?: Function, placeholder?: string },
 *   showSearch?: boolean,
 *   notifications?: { hasUnread?: boolean, to?: string, onClick?: () => void },
 *   primaryAction?: { label: string, to?: string, onClick?: () => void, icon?: object },
 *   utilityBarExtras?: React.ReactNode,
 *   wide?: boolean,
 *   className?: string,
 *   contentClassName?: string
 * }} props
 */
export function ConsoleShell({
  children,
  variant = 'console',
  navGroups,
  breadcrumbs = [],
  title,
  search,
  showSearch = true,
  notifications,
  primaryAction,
  utilityBarExtras,
  wide = true,
  className,
  contentClassName,
}) {
  const { isDesktop } = useViewport();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const isAdmin = variant === 'admin';
  const groups = navGroups || (isAdmin ? ADMIN_NAV : CONSOLE_NAV);
  const badge = isAdmin ? 'Admin' : undefined;
  const navLabel = isAdmin ? 'Admin' : 'Console';

  if (isDesktop) {
    return (
      <div className={cn('dsk-body', 'dsk-shell', className)}>
        <SkipLink track="desktop" />
        <Sidebar groups={groups} badge={badge} label={navLabel} />
        <div className="dsk-main">
          <UtilityBar
            breadcrumbs={breadcrumbs}
            search={search}
            showSearch={showSearch}
            notifications={notifications}
            primaryAction={primaryAction}
          >
            {utilityBarExtras}
          </UtilityBar>
          <main
            id={MAIN_ID}
            className={cn('dsk-page', wide && 'dsk-page--console', contentClassName)}
          >
            {children}
          </main>
        </div>
      </div>
    );
  }

  const mobileTitle =
    title || (breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : navLabel);

  // The utility bar's search follows the sidebar into the drawer rather than
  // competing with the page title for a 44px header row.
  const drawerSearch = showSearch ? (
    <div className="ds-navdrawer__search">
      {/* A DIV, not a LABEL: the clear button below is a real button, and a
          button inside a <label> is activated twice by one click. The input
          already carries its own `aria-label`. */}
      <div className="ds-search">
        <Glyph name="search" />
        <input
          className="ds-search__input"
          type="search"
          aria-label={`Search the ${navLabel.toLowerCase()}`}
          placeholder={search?.placeholder || 'Search your listings and orders'}
          value={search?.value}
          onChange={search?.onChange ? (event) => search.onChange(event.target.value) : undefined}
          readOnly={search?.value !== undefined && !search?.onChange}
        />
        {search?.value && search?.onChange ? (
          <button
            type="button"
            className="dsa-search-clear"
            aria-label="Clear search"
            onClick={() => search.onChange('')}
          >
            <Glyph name="x" />
          </button>
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <div className={cn('ds-appshell', className)}>
      <SkipLink track="mobile" />
      <MobileTopBar
        title={mobileTitle}
        menu={{
          onClick: () => setDrawerOpen(true),
          label: `Open ${navLabel.toLowerCase()} navigation`,
          controls: DRAWER_ID,
          expanded: drawerOpen,
        }}
        end={
          <>
            <ThemeToggle track="mobile" />
            {primaryAction &&
              (primaryAction.to ? (
                <Link
                  to={primaryAction.to}
                  className="ds-iconbtn ds-iconbtn--bare"
                  aria-label={primaryAction.label}
                >
                  <Glyph name="plus" />
                </Link>
              ) : (
                <button
                  type="button"
                  className="ds-iconbtn ds-iconbtn--bare"
                  onClick={primaryAction.onClick}
                  aria-label={primaryAction.label}
                >
                  <Glyph name="plus" />
                </button>
              ))}
          </>
        }
      />
      <main id={MAIN_ID} className={cn('ds-appshell__main', contentClassName)}>
        {children}
      </main>
      <NavDrawer
        id={DRAWER_ID}
        open={drawerOpen}
        onClose={closeDrawer}
        groups={groups}
        badge={badge}
        label={navLabel}
        header={drawerSearch}
      />
    </div>
  );
}

export default ConsoleShell;
